import React, { useState } from "react";
import { observer } from "mobx-react";
import { loginController } from "../modules/controllers";
import {
  TextField,
  Button,
  Typography,
  Box,
  Paper,
  Alert,
} from "@mui/material";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { colors } from "../theme";

const LoginContainer = styled.div`
  min-height: 100vh;
  background: ${colors.primaryBackground};
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(
        ellipse at 20% 10%,
        rgba(204, 78, 48, 0.1) 0%,
        transparent 55%
      ),
      radial-gradient(
        ellipse at 80% 90%,
        rgba(204, 78, 48, 0.06) 0%,
        transparent 55%
      );
    pointer-events: none;
  }
`;

const LoginCard = styled(Paper)`
  padding: 48px;
  max-width: 480px;
  width: 100%;
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(44, 24, 16, 0.1), 0 2px 8px rgba(204, 78, 48, 0.08);
  text-align: center;
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.98);
  position: relative;
  z-index: 1;

  @media (max-width: 600px) {
    padding: 32px 24px;
    margin: 16px;
  }
`;

const LogoContainer = styled.div`
  margin-bottom: 32px;
`;

const LogoImage = styled.img`
  height: 90px;
  width: auto;
  margin-bottom: 20px;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1));
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.02);
  }

  @media (max-width: 600px) {
    height: 75px;
    margin-bottom: 16px;
  }
`;

const LoginTitle = styled(Typography)`
  font-size: 2.2rem;
  font-weight: 700;
  color: ${colors.primary};
  margin-bottom: 8px;
  font-family: "Montserrat", sans-serif;
  letter-spacing: -0.02em;

  @media (max-width: 600px) {
    font-size: 1.8rem;
  }
`;

const LoginSubtitle = styled(Typography)`
  color: ${colors.text.secondary};
  margin-bottom: 40px;
  font-family: "Montserrat", sans-serif;
  font-weight: 400;
  line-height: 1.5;

  @media (max-width: 600px) {
    margin-bottom: 32px;
    font-size: 0.9rem;
  }
`;

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const StyledTextField = styled(TextField)`
  .MuiOutlinedInput-root {
    border-radius: 12px;

    &:hover .MuiOutlinedInput-notchedOutline {
      border-color: ${colors.primary};
    }

    &.Mui-focused .MuiOutlinedInput-notchedOutline {
      border-color: ${colors.primary};
    }
  }

  .MuiInputLabel-root.Mui-focused {
    color: ${colors.primary};
  }
`;

const LoginButton = styled(Button)`
  padding: 18px;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 14px;
  background: ${colors.gradients.primary} !important;
  text-transform: none;
  font-family: "Montserrat", sans-serif;
  box-shadow: 0 6px 20px rgba(204, 78, 48, 0.3);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(204, 78, 48, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
`;

const SignUpButton = styled(Button)`
  padding: 18px;
  font-size: 1.1rem;
  font-weight: 500;
  border-radius: 14px;
  border: 2px solid ${colors.primary};
  color: ${colors.primary};
  text-transform: none;
  font-family: "Montserrat", sans-serif;
  transition: all 0.3s ease;

  &:hover {
    border-color: ${colors.primary};
    background-color: rgba(204, 78, 48, 0.06);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(204, 78, 48, 0.2);
  }

  &:active {
    transform: translateY(0);
  }
`;

export const Login = observer(() => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const navigate = useNavigate();

  const handleSubmit = (e) => {
    e.preventDefault();
    loginController.login(username, password);
  };

  return (
    <LoginContainer>
      <LoginCard elevation={0}>
        <LogoContainer>
          <LogoImage src="/logo_vertical.png" alt="Culinary IQ Logo" />
          <LoginTitle variant="h2">Welcome Back</LoginTitle>
          <LoginSubtitle variant="subtitle1">
            Sign in to continue your culinary journey
          </LoginSubtitle>
        </LogoContainer>

        {loginController.getLoginMessage && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {loginController.getLoginMessage}
          </Alert>
        )}

        <FormContainer onSubmit={handleSubmit}>
          <StyledTextField
            type="text"
            label="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            variant="outlined"
            fullWidth
            required
          />
          <StyledTextField
            type="password"
            label="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            variant="outlined"
            fullWidth
            required
          />
          <LoginButton type="submit" variant="contained" fullWidth size="large">
            Sign In
          </LoginButton>
          <SignUpButton
            onClick={() => navigate("/register")}
            variant="outlined"
            fullWidth
            size="large"
          >
            Create New Account
          </SignUpButton>
        </FormContainer>

        <Box sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: "divider" }}>
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{
              fontFamily: '"Montserrat", sans-serif',
              fontWeight: 400,
              fontSize: "0.875rem",
            }}
          >
            Not a member yet?{" "}
            <Box
              component="span"
              onClick={() => navigate("/")}
              sx={{
                color: colors.primary,
                fontWeight: 600,
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              Join the waitlist
            </Box>
          </Typography>
        </Box>
      </LoginCard>
    </LoginContainer>
  );
});
